/**
 * 下拉选择
 * ---------------------------------------------------------------------------
 * 替换原生 `<select>`。原生控件的问题在门店场景里很具体：
 *
 *   1. 展开后是**操作系统**的样式，深色、系统字体，和设计稿完全不搭，
 *      而且 Windows / macOS 长得还不一样，截图对不上。
 *   2. **不能搜索**。员工、项目、商品这些列表动辄几十上百条，
 *      前台要在收银时按下拉一条条找——同名员工（比如两个"林森"）更是只能靠位置猜。
 *   3. 选项文字长了会被系统截断，没法自定义副文案。
 *
 * ## 为什么底下还压一个真实的 `<select>`
 *
 * 全项目有 29 处在用它，调用点写的都是 `onChange={(e) => ...e.target.value}`。
 * 如果改成自定义回调，29 个文件都要动，而且要伪造一个假的 event 对象。
 *
 * 所以这里保留一个视觉隐藏的原生 `<select>` 持有真实值：选中时用原生 setter
 * 改它的 value 再派发真的 change 事件，React 的 onChange 收到的就是**真事件**，
 * 不是伪造的。`name` / `required` / 表单提交这些原生语义也一并保住。
 * 调用点一行都不用改。
 *
 * ## 无障碍
 *
 * 触发器是 `role="combobox"`，展开后是 `role="listbox"`，
 * 高亮项通过 `aria-activedescendant` 告诉读屏软件——焦点始终留在搜索框里，
 * 这样才能边打字边用上下键选。原生 select 的键盘习惯（↑↓ Enter Esc）全部保留。
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Popover } from 'radix-ui';
import { Icon } from '@/components/icons';
import { cn } from '@/lib/cn';
import { matchesKeyword } from '@/lib/pinyin';

type OptionItem = {
  value: string;
  label: string;
  disabled: boolean;
};

/**
 * 把 `<option>` 的子节点拍平成纯文本。
 *
 * 不能用 `String(children)`：写成 `{name} · {minutes} 分钟` 时 React 传进来的是
 * **数组**，而 `Array.prototype.toString` 用逗号连接，结果会变成
 * `头皮护理, · ,60, 分钟` —— 每段之间凭空多出逗号。
 * 这里逐段递归拼接，不加任何分隔符。
 */
function toText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (typeof node === 'object' && 'props' in node) {
    return toText((node as { props?: { children?: ReactNode } }).props?.children);
  }
  return '';
}

/**
 * 从 `<option>` 子节点里读出选项。
 * 用 children 而不是 options 数组，是为了和原生 select 的调用方式完全一致。
 */
function readOptions(children: ReactNode): OptionItem[] {
  const result: OptionItem[] = [];
  const walk = (node: ReactNode): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== 'object' || !('props' in node)) return;
    const element = node as {
      type?: unknown;
      props?: { value?: unknown; children?: ReactNode; disabled?: boolean };
    };
    // 只认 <option>，<optgroup> 目前项目里没有用到，遇到就走下一层
    if (element.type === 'option') {
      const label = toText(element.props?.children);
      result.push({
        value: String(element.props?.value ?? label),
        label,
        disabled: Boolean(element.props?.disabled),
      });
      return;
    }
    if (element.props?.children) walk(element.props.children);
  };
  walk(children);
  return result;
}

/**
 * 改原生 select 的值并派发真事件。
 *
 * 必须用原型上的 setter：React 会在元素实例上覆写 value 的 setter 来做受控，
 * 直接 `el.value = x` 改不到 React 记录的那份旧值，随后派发的 change 会被
 * React 当成"值没变"而忽略掉。
 */
function commitValue(select: HTMLSelectElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLSelectElement.prototype,
    'value',
  )?.set;
  setter?.call(select, value);
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

export function Select({
  className,
  children,
  value,
  defaultValue,
  disabled,
  placeholder = '请选择',
  // 无障碍名字要挂在**可见的** combobox 输入框上，不能跟着 ...rest 落到
  // 隐藏的原生 select 上——读屏软件读的是前者，后者是 aria-hidden 的。
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  /** 没有选中项时触发器上显示的文字 */
  placeholder?: string;
}) {
  const options = useMemo(() => readOptions(children), [children]);
  const nativeRef = useRef<HTMLSelectElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const currentValue = String(value ?? defaultValue ?? '');
  const selected = options.find((option) => option.value === currentValue);

  // 支持拼音首字母：输入 az 命中"阿哲"，xjc 命中"洗剪吹"（见 lib/pinyin.ts）
  const filtered = useMemo(
    () => options.filter((option) => matchesKeyword(option.label, keyword)),
    [options, keyword],
  );

  // 每次打开都从选中项开始，而不是停在上次的位置
  useEffect(() => {
    if (!open) {
      setKeyword('');
      return;
    }
    const index = filtered.findIndex((option) => option.value === currentValue);
    setActiveIndex(index >= 0 ? index : 0);
    // 打开后把焦点交给搜索框，前台可以直接打字筛选
    requestAnimationFrame(() => searchRef.current?.focus());
    // filtered 只在打开这一刻取一次，后续输入由 onChange 自己重置高亮
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (option: OptionItem) => {
    if (option.disabled || !nativeRef.current) return;
    commitValue(nativeRef.current, option.value);
    setOpen(false);
  };


  function handleKeyDown(event: React.KeyboardEvent) {
    // 收起状态：方向键 / 回车 / 空格打开；直接打可见字符时，用这一次的按键
    // 作为关键字的**第一个字符**——不能让它落进 input 里，否则会拼在已选文案
    // 后面（"阿哲" + "l" = "阿哲l"），永远筛不出东西。这就是"要填两次"的原因。
    if (!open) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        setKeyword('');
        setOpen(true);
        return;
      }
      if (
        event.key.length === 1 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        setKeyword(event.key);
        setActiveIndex(0);
        setOpen(true);
      }
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (filtered.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // 在可选项之间跳，跳过 disabled，绕回首尾
      let next = activeIndex;
      for (let i = 0; i < filtered.length; i += 1) {
        next = (next + step + filtered.length) % filtered.length;
        if (!filtered[next].disabled) break;
      }
      setActiveIndex(next);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) choose(option);
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActiveIndex(event.key === 'Home' ? 0 : filtered.length - 1);
    }
  }

  return (
    <>
      {/*
        真实的值住在这里。视觉上隐藏但**不能**用 display:none 或 hidden，
        否则表单校验（required）在某些浏览器上会报"不可聚焦的元素无效"。
      */}
      <select
        ref={nativeRef}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute size-0 overflow-hidden opacity-0"
        {...rest}
      >
        {children}
      </select>

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Anchor asChild>
          <div
            ref={anchorRef}
            className={cn(
              'flex h-[38px] w-full items-center gap-2 rounded-btn border border-line bg-panel-2 px-3 text-body',
              'transition-[border-color,box-shadow]',
              'focus-within:border-brand focus-within:bg-panel focus-within:shadow-[0_0_0_3px_var(--color-brand-pale)]',
              disabled && 'cursor-not-allowed opacity-60',
              open && 'border-brand bg-panel',
              className,
            )}
            onPointerDown={(event) => {
              if (disabled) return;
              // 展开时点输入框内部：只是想挪光标，别关掉
              if (open && event.target === searchRef.current) return;
              // 其余情况（收起时点任意处、展开时点箭头或空白）都是切换。
              // preventDefault 掉，避免浏览器把焦点从 input 上抢走。
              event.preventDefault();
              setOpen((value) => !value);
              searchRef.current?.focus();
            }}
          >
            {/*
              输入框就在原位，不再在下拉里另起一行——展开后视线不用移动，
              也不会出现"上面显示已选、下面又有个空输入框"的割裂感。

              收起时 readOnly：否则打字会直接拼在已选文案后面（"阿哲"+"l"），
              永远筛不出东西，用户只能删掉重打——这就是"要填两次"。
              收起状态下的按键由 handleKeyDown 接管，用第一个按键作为关键字起点。
            */}
            <input
              ref={searchRef}
              role="combobox"
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                open && filtered[activeIndex]
                  ? `${listId}-${activeIndex}`
                  : undefined
              }
              disabled={disabled}
              readOnly={!open}
              value={open ? keyword : (selected?.label ?? '')}
              placeholder={open ? (selected?.label ?? placeholder) : placeholder}
              onChange={(event) => {
                setKeyword(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              className={cn(
                'min-w-0 flex-1 bg-transparent outline-none',
                'placeholder:text-ink-3 disabled:cursor-not-allowed',
                // 收起时它只是个显示位，不给文本光标，行为更像原来的下拉
                !open && 'cursor-pointer caret-transparent',
              )}
            />
            <Icon
              name="chevd"
              size={14}
              className={cn(
                'shrink-0 text-ink-3 transition-transform',
                open && 'rotate-180',
              )}
            />
          </div>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            // 宽度跟随触发器，避免下拉比输入框窄一截显得歪
            className="z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-md border border-line bg-panel shadow-e3"
            // 焦点必须留在触发器的输入框里，否则展开后就打不了字了
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            /*
              触发器在浮层之外，Radix 默认把点它算成"点外面"并自动关闭；
              而我的 onPointerDown 同时也会 toggle。两者叠加就是
              "选中后再点一下，弹出又立刻消失"。这里告诉 Radix 忽略触发器内的
              交互，开关只由 onPointerDown 一处决定。
            */
            onInteractOutside={(event) => {
              if (anchorRef.current?.contains(event.target as Node)) {
                event.preventDefault();
              }
            }}
          >
            <div
              id={listId}
              role="listbox"
              className="scroll-thin max-h-[248px] overflow-y-auto p-1"
            >
              {filtered.length === 0 ? (
                <p className="px-3 py-6 text-center text-note text-ink-3">
                  没有匹配的选项
                </p>
              ) : (
                filtered.map((option, index) => {
                  const isSelected = option.value === currentValue;
                  const isActive = index === activeIndex;
                  return (
                    <div
                      key={option.value || `empty-${index}`}
                      id={`${listId}-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      onClick={() => choose(option)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-2 text-table',
                        isActive && 'bg-brand-pale text-brand-2',
                        option.disabled &&
                          'cursor-not-allowed text-ink-3 opacity-60',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {option.label}
                      </span>
                      {/* 选中态不能只靠背景色：高亮的是键盘位置，打勾的才是已选 */}
                      {isSelected && (
                        <Icon
                          name="check"
                          size={14}
                          className="shrink-0 text-brand-2"
                        />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}
